'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import { proposalsApi } from '@/src/shared/api/proposals.api';
import { cn } from '@/src/shared/lib/utils';

interface SubmitProposalModalProps extends BaseModalProps {
  orderId?: string;
  orderTitle?: string;
  clientName?: string;
  proposalId?: string;
  existingProposal?: {
    proposedPrice: number;
    message: string;
    estimatedDays?: number | null;
  };
  onSuccess?: () => void;
}

/**
 * Submit Proposal Modal - allows workers to submit or edit proposals for client orders
 * Usage:
 * - New: const { open } = useModal('submit-proposal'); open({ orderId: '123', orderTitle: 'Task', clientName: 'John' });
 * - Edit: open({ orderId: '123', proposalId: '456', existingProposal: {...} });
 */
export function SubmitProposalModal({
  isOpen,
  onClose,
  orderId,
  orderTitle,
  clientName,
  proposalId,
  existingProposal,
  onSuccess,
}: SubmitProposalModalProps) {
  const t = useTranslations('clientOrders');

  const isEditMode = !!proposalId && !!existingProposal;

  const [proposedPrice, setProposedPrice] = useState<string>(
    existingProposal?.proposedPrice?.toString() || ''
  );
  const [message, setMessage] = useState<string>(existingProposal?.message || '');
  const [estimatedDays, setEstimatedDays] = useState<string>(
    existingProposal?.estimatedDays?.toString() || ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parsedPrice = proposedPrice !== '' ? parseFloat(proposedPrice) : null;
  const isPriceTooHigh = parsedPrice !== null && parsedPrice > 99999999;
  const isValidPrice = parsedPrice !== null && parsedPrice > 0 && !isPriceTooHigh;
  const isValidMessage = message.trim().length > 0;
  const isFormValid = isValidPrice && isValidMessage;

  // Update form values when existingProposal changes (edit mode)
  useEffect(() => {
    if (isOpen && existingProposal) {
      setProposedPrice(existingProposal.proposedPrice.toString());
      setMessage(existingProposal.message);
      setEstimatedDays(existingProposal.estimatedDays?.toString() || '');
    } else if (isOpen && !existingProposal) {
      // Reset form when opening in create mode
      setProposedPrice('');
      setMessage('');
      setEstimatedDays('');
    }
  }, [isOpen, existingProposal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      if (isEditMode && proposalId) {
        // Update existing proposal
        await proposalsApi.update(proposalId, {
          proposedPrice: parseFloat(proposedPrice),
          message: message.trim(),
          estimatedDays: estimatedDays ? parseInt(estimatedDays, 10) : null,
        });
        toast.success('Proposal updated successfully');
      } else if (orderId) {
        // Create new proposal
        await proposalsApi.create(orderId, {
          proposedPrice: parseFloat(proposedPrice),
          message: message.trim(),
          estimatedDays: estimatedDays ? parseInt(estimatedDays, 10) : null,
        });
        toast.success(t('respondSuccess'));
      }

      onSuccess?.();
      onClose();

      // Reset form
      setProposedPrice('');
      setMessage('');
      setEstimatedDays('');
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'ALREADY_SUBMITTED') {
          toast.error(t('alreadySubmitted'));
        } else {
          toast.error(isEditMode ? 'Failed to update proposal' : t('respondError'));
        }
      } else {
        toast.error(isEditMode ? 'Failed to update proposal' : t('respondError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      animation="slide-up"
      title={isEditMode ? 'Edit Proposal' : t('proposalModal.title')}
      description={orderTitle ? `"${orderTitle}"${clientName ? ` - ${clientName}` : ''}` : undefined}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Proposed Price */}
        <div>
          <label
            htmlFor="proposedPrice"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t('proposalModal.priceLabel')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
            <input
              id="proposedPrice"
              type="number"
              min="1"
              max="99999999"
              step="0.01"
              value={proposedPrice}
              onChange={(e) => setProposedPrice(e.target.value)}
              className={cn(
                'w-full pl-8 pr-4 py-3 border rounded-lg',
                'focus:outline-none focus:ring-2 focus:ring-[#6AB344] focus:border-transparent',
                'transition-colors',
                !isValidPrice && proposedPrice !== '' ? 'border-red-300' : 'border-gray-300'
              )}
              placeholder={t('proposalModal.pricePlaceholder')}
              required
              disabled={isSubmitting}
            />
          </div>
          {isPriceTooHigh && (
            <p className="mt-1 text-xs text-red-500">Price cannot exceed $99,999,999 CAD</p>
          )}
        </div>

        {/* Message / Cover Letter */}
        <div>
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t('proposalModal.messageLabel')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className={cn(
              'w-full px-4 py-3 border rounded-lg resize-none',
              'focus:outline-none focus:ring-2 focus:ring-[#6AB344] focus:border-transparent',
              'transition-colors',
              !isValidMessage && message !== '' ? 'border-red-300' : 'border-gray-300'
            )}
            placeholder={t('proposalModal.messagePlaceholder')}
            maxLength={2000}
            required
            disabled={isSubmitting}
          />
          <p className="mt-1 text-xs text-gray-500">
            {message.length}/2000
          </p>
        </div>

        {/* Estimated Days (Optional) */}
        <div>
          <label
            htmlFor="estimatedDays"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {t('proposalModal.estimatedDaysLabel')}
          </label>
          <input
            id="estimatedDays"
            type="number"
            min="1"
            max="365"
            value={estimatedDays}
            onChange={(e) => setEstimatedDays(e.target.value)}
            className={cn(
              'w-full px-4 py-3 border border-gray-300 rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-[#6AB344] focus:border-transparent',
              'transition-colors'
            )}
            placeholder={t('proposalModal.estimatedDaysPlaceholder')}
            disabled={isSubmitting}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            className={cn(
              'flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg',
              'hover:bg-gray-50 transition-colors font-medium',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            disabled={isSubmitting}
          >
            {t('proposalModal.cancel')}
          </button>
          <button
            type="submit"
            disabled={!isFormValid || isSubmitting}
            className={cn(
              'flex-1 px-4 py-3 rounded-lg font-medium transition-colors',
              'bg-[#6AB344] text-white',
              'hover:bg-[#5A9B38]',
              'disabled:bg-gray-300 disabled:cursor-not-allowed'
            )}
          >
            {isSubmitting
              ? (isEditMode ? 'Updating...' : t('proposalModal.submitting'))
              : (isEditMode ? 'Update Proposal' : t('proposalModal.submit'))
            }
          </button>
        </div>
      </form>
    </Modal>
  );
}
